import {
  CopyableCodeBlock,
  CopyableValue,
  DescriptionList,
  Stack,
  Tabs,
  Tag,
  Title,
} from "./controls";
import { useFeedback } from "./feedback";
import "./FaaSFunctionDetail.css";

export interface FaaSSkillDetailData {
  functionSlug: string;
  functionVersion: string;
  manifest: unknown;
  manifestDigest: string;
  skillRef: string;
  skill: {
    content: string;
    contentDigest: string;
    description: string;
    entrypoint: string;
    requiredBundles: Array<{ slug: string; version: string }>;
    requiredTools: string[];
    title: string;
  };
}

export function FaaSSkillDetail({ detail }: { detail: FaaSSkillDetailData }) {
  const { message } = useFeedback();
  const copyFeedback = {
    onCopied: () => void message.success("已复制"),
    onCopyError: () => void message.error("复制失败"),
  };
  return (
    <section className="faas-detail faas-skill-detail" aria-label="技能详情">
      <header className="faas-detail-header">
        <Title level={4}>{detail.skill.title || detail.functionSlug}</Title>
        <Tag>{detail.functionVersion}</Tag>
      </header>
      <Tabs
        aria-label="技能详情选项卡"
        items={[
          {
            key: "overview",
            label: "概览",
            children: (
              <>
                <p className="faas-detail-description">
                  {detail.skill.description}
                </p>
                <DescriptionList
                  className="faas-detail-metadata"
                  grouped
                  items={[
                    {
                      key: "skill-ref",
                      term: "Skill Ref",
                      detail: (
                        <CopyableValue
                          {...copyFeedback}
                          label="Skill Ref"
                          value={detail.skillRef}
                        />
                      ),
                    },
                    {
                      key: "version",
                      term: "固定版本",
                      detail: <code>{detail.functionVersion}</code>,
                    },
                    {
                      key: "slug",
                      term: "函数标识",
                      detail: <code>{detail.functionSlug}</code>,
                    },
                    {
                      key: "entrypoint",
                      term: "入口",
                      detail: <code>{detail.skill.entrypoint}</code>,
                    },
                    {
                      key: "content-digest",
                      term: "内容摘要",
                      detail: (
                        <CopyableValue
                          {...copyFeedback}
                          label="内容摘要"
                          value={detail.skill.contentDigest}
                        />
                      ),
                      itemProps: { className: "faas-detail-wide" },
                    },
                    {
                      key: "manifest-digest",
                      term: "Manifest 摘要",
                      detail: (
                        <CopyableValue
                          {...copyFeedback}
                          label="Manifest 摘要"
                          value={detail.manifestDigest}
                        />
                      ),
                      itemProps: { className: "faas-detail-wide" },
                    },
                    {
                      key: "bundles",
                      term: "依赖函数组",
                      detail: (
                        <Stack gap={4} wrap>
                          {detail.skill.requiredBundles.length
                            ? detail.skill.requiredBundles.map((item) => (
                                <Tag key={`${item.slug}@${item.version}`}>
                                  {item.slug}@{item.version}
                                </Tag>
                              ))
                            : "-"}
                        </Stack>
                      ),
                      itemProps: { className: "faas-detail-wide" },
                    },
                    {
                      key: "tools",
                      term: "依赖工具",
                      detail: (
                        <Stack gap={4} wrap>
                          {detail.skill.requiredTools.length
                            ? detail.skill.requiredTools.map((item) => (
                                <Tag key={item}>{item}</Tag>
                              ))
                            : "-"}
                        </Stack>
                      ),
                      itemProps: { className: "faas-detail-wide" },
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: "content",
            label: "SKILL.md",
            children: (
              <CopyableCodeBlock
                {...copyFeedback}
                content={detail.skill.content}
                label="SKILL.md"
                maxHeight={560}
                wrap
              />
            ),
          },
          {
            key: "manifest",
            label: "Manifest",
            children: (
              <CopyableCodeBlock
                {...copyFeedback}
                content={JSON.stringify(detail.manifest ?? {}, null, 2)}
                label="function.manifest.json"
                maxHeight={560}
                wrap
              />
            ),
          },
        ]}
      />
    </section>
  );
}
